<!--
 Copyright 2022 by domX Private Company

 All rights reserved,
 also regarding any disposal, exploitation, reproduction,
 editing, distribution, as well as in the event of
 applications for industrial property rights.

 This software contains proprietary and confidential
 information of domX Private Company.
 The disclosure of confidential information without
 express authorization is prohibited.

 This software may include Open Source Software.
 As far as Open Source Software is included,
 your rights for the respective Open Source Software
 components are governed by the applicable
 Open Source Software licenses.
-->

<template class="fill-height">
  <v-expansion-panels  accordion multiple v-model="expansionPanel">
      <v-expansion-panel key="0" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-heat-pump-outline</v-icon>
            <span>{{ $t("heatpump_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.ctl_mod_t_ch1 !== undefined"
              :value="heatpumpCtlMod"
              :title_number="$t('heatpump_operation_status')"
              :subTitle="$t('heatpump_control_subtitle')"
              text_font_size="18px"
              control="heating_control"
              @heatingControlValChange="heatpumpControlChange"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSelectCard
              v-if="deviceData.mb.op_mode_set !== undefined"
              :value="deviceData.mb.op_mode_set"
              cols="4"
              controlVariable="op_mode_set"
              :titleNumber="$t('op_mode_set')"
              textFontSize="18px"
              control="other"
              :itemList="controlModeList"
              itemText="text"
              itemValue="value"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSelectCard>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.ctl_mod_dhw !== undefined"
              :value="DHWStatus"
              :title_number="$t('dhw_control')"
              :subTitle="$t('dhw_control_subtitle')"
              text_font_size="18px"
              control="hot_water_control"
              @hotWaterControlValChange="DHWControlChange"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.t_ch1_set !== undefined"
              :key="`t-ch1-set-${tCh1SetLimits.min}-${tCh1SetLimits.max}-${deviceData.mb.op_mode_set ?? deviceData.mb.op_mode}-${deviceData.mb.c_ter_typ_ch1 ?? deviceData.mb.h_ter_typ_ch1}`"
              v-model="deviceData.mb.t_ch1_set"
              :min="tCh1SetLimits.min"
              :max="tCh1SetLimits.max"
              :step="1"
              :decimals="0"
              controlVariable="t_ch1_set"
              :title_number="$t('t_ch1_set')"
              :disabled="tCh1SetDisabled"
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.t_ch2_set !== undefined"
              v-model="deviceData.mb.t_ch2_set"
              :min="deviceActionLimits.t_ch2_set.min || 10"
              :max="deviceActionLimits.t_ch2_set.max || 85"
              :step="1"
              :decimals="0"
              controlVariable="t_ch2_set"
              :title_number="$t('t_ch2_set')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.t_r_en !== undefined"
              :value="deviceData.mb.t_r_en == 0 ? false : true"
              :title_number="$t('t_r_en')"
              text_font_size="18px"
              control="other"
              :subTitle="$t('t_r_en_subtitle')"
              controlVariable="t_r_en"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel key="1" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-fire</v-icon>
            <span>{{ $t("heating_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.heat_set !== undefined"
              :value="deviceData.mb.heat_set == 0 ? false : true"
              :title_number="$t('heat_set')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              controlVariable="heat_set"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSelectCard
              v-if="deviceData.mb.h_ter_typ_ch1 !== undefined"
              :value="deviceData.mb.h_ter_typ_ch1"
              cols="4"
              controlVariable="h_ter_typ_ch1"
              :titleNumber="$t('h_ter_typ_ch1')"
              textFontSize="18px"
              control="other"
              :itemList="terminalUnitList"
              itemText="text"
              itemValue="value"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSelectCard>
            <DeviceOverviewSelectCard
              v-if="deviceData.mb.h_ter_typ_ch2 !== undefined"
              :value="deviceData.mb.h_ter_typ_ch2"
              cols="4"
              controlVariable="h_ter_typ_ch2"
              :titleNumber="$t('h_ter_typ_ch2')"
              textFontSize="18px"
              control="other"
              :itemList="terminalUnitList"
              itemText="text"
              itemValue="value"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSelectCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_amb_max !== undefined"
              v-model="deviceData.mb.h_t_amb_max"
              :min="deviceActionLimits.h_t_amb_max.min || 20"
              :max="deviceActionLimits.h_t_amb_max.max || 35"
              :step="1"
              :decimals="0"
              controlVariable="h_t_amb_max"
              :title_number="$t('h_t_amb_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_amb_min !== undefined"
              v-model="deviceData.mb.h_t_amb_min"
              :min="deviceActionLimits.h_t_amb_min.min || -25"
              :max="deviceActionLimits.h_t_amb_min.max || 30"
              :step="1"
              :decimals="0"
              controlVariable="h_t_amb_min"
              :title_number="$t('h_t_amb_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_dt_on !== undefined"
              v-model="deviceData.mb.h_dt_on"
              :min="deviceActionLimits.h_dt_on.min || 2"
              :max="deviceActionLimits.h_dt_on.max || 20"
              :step="1"
              :decimals="0"
              controlVariable="h_dt_on"
              :title_number="$t('h_dt_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_dt_out_on !== undefined"
              v-model="deviceData.mb.h_dt_out_on"
              :min="deviceActionLimits.h_dt_out_on.min || 1"
              :max="deviceActionLimits.h_dt_out_on.max || 10"
              :step="1"
              :decimals="0"
              controlVariable="h_dt_out_on"
              :title_number="$t('h_dt_out_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_cur_set_max !== undefined"
              v-model="deviceData.mb.h_t_cur_set_max"
              :min="heatingSetLimits.min"
              :max="heatingSetLimits.max"
              :step="1"
              :decimals="0"
              controlVariable="h_t_cur_set_max"
              :title_number="$t('h_t_cur_set_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_cur_set_min !== undefined"
              v-model="deviceData.mb.h_t_cur_set_min"
              :min="heatingSetLimits.min"
              :max="heatingSetLimits.max"
              :step="1"
              :decimals="0"
              controlVariable="h_t_cur_set_min"
              :title_number="$t('h_t_cur_set_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_out_min !== undefined"
              v-model="deviceData.mb.h_t_out_min"
              :min="deviceActionLimits?.h_t_out_min?.min || -25"
              :max="deviceActionLimits?.h_t_out_min?.max || 35"
              :step="1"
              :decimals="0"
              controlVariable="h_t_out_min"
              :title_number="$t('h_t_out_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_t_out_max !== undefined"
              v-model="deviceData.mb.h_t_out_max"
              :min="deviceActionLimits?.h_t_out_max?.min || -25"
              :max="deviceActionLimits?.h_t_out_max?.max || 35"
              :step="1"
              :decimals="0"
              controlVariable="h_t_out_max"
              :title_number="$t('h_t_out_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel key="2" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-snowflake</v-icon>
            <span>{{ $t("cooling_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.cool_set !== undefined"
              :value="deviceData.mb.cool_set == 0 ? false : true"
              :title_number="$t('cool_set')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              controlVariable="cool_set"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSelectCard
              v-if="deviceData.mb.c_ter_typ_ch1 !== undefined"
              :value="deviceData.mb.c_ter_typ_ch1"
              cols="4"
              controlVariable="c_ter_typ_ch1"
              :titleNumber="$t('c_ter_typ_ch1')"
              textFontSize="18px"
              control="other"
              itemText="text"
              itemValue="value"
              :itemList="terminalUnitList"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSelectCard>
            <DeviceOverviewSelectCard
              v-if="deviceData.mb.c_ter_typ_ch2 !== undefined"
              :value="deviceData.mb.c_ter_typ_ch2"
              cols="4"
              controlVariable="c_ter_typ_ch2"
              :titleNumber="$t('c_ter_typ_ch2')"
              textFontSize="18px"
              control="other"
              itemText="text"
              itemValue="value"
              :itemList="terminalUnitList"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSelectCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_amb_max !== undefined"
              v-model="deviceData.mb.c_t_amb_max"
              :min="deviceActionLimits?.c_t_amb_max?.min || 35"
              :max="deviceActionLimits?.c_t_amb_max?.max || 52"
              :step="1"
              :decimals="0"
              controlVariable="c_t_amb_max"
              :title_number="$t('c_t_amb_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_amb_min !== undefined"
              v-model="deviceData.mb.c_t_amb_min"
              :min="deviceActionLimits?.c_t_amb_min?.min || -5"
              :max="deviceActionLimits?.c_t_amb_min?.max || 25"
              :step="1"
              :decimals="0"
              controlVariable="c_t_amb_min"
              :title_number="$t('c_t_amb_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_dt_on !== undefined"
              v-model="deviceData.mb.c_dt_on"
              :min="deviceActionLimits?.c_dt_on?.min || 2"
              :max="deviceActionLimits?.c_dt_on?.max || 10"
              :step="1"
              :decimals="0"
              controlVariable="c_dt_on"
              :title_number="$t('c_dt_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_dt_out_on !== undefined"
              v-model="deviceData.mb.c_dt_out_on"
              :min="deviceActionLimits?.c_dt_out_on?.min || 1"
              :max="deviceActionLimits?.c_dt_out_on?.max || 10"
              :step="1"
              :decimals="0"
              controlVariable="c_dt_out_on"
              :title_number="$t('c_dt_out_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_cur_set_max !== undefined"
              v-model="deviceData.mb.c_t_cur_set_max"
              :min="coolingSetLimits.min"
              :max="coolingSetLimits.max"
              :step="1"
              :decimals="0"
              controlVariable="c_t_cur_set_max"
              :title_number="$t('c_t_cur_set_max')"
              :disabled="coolingSetDisabled"
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_cur_set_min !== undefined"
              v-model="deviceData.mb.c_t_cur_set_min"
              :min="coolingSetLimits.min"
              :max="coolingSetLimits.max"
              :step="1"
              :decimals="0"
              controlVariable="c_t_cur_set_min"
              :title_number="$t('c_t_cur_set_min')"
              :disabled="coolingSetDisabled"
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_out_min !== undefined"
              v-model="deviceData.mb.c_t_out_min"
              :min="deviceActionLimits?.c_t_out_min?.min || -5"
              :max="deviceActionLimits?.c_t_out_min?.max || 46"
              :step="1"
              :decimals="0"
              controlVariable="c_t_out_min"
              :title_number="$t('c_t_out_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_t_out_max !== undefined"
              v-model="deviceData.mb.c_t_out_max"
              :min="deviceActionLimits?.c_t_out_max?.min || -5"
              :max="deviceActionLimits?.c_t_out_max?.max || 46"
              :step="1"
              :decimals="0"
              controlVariable="c_t_out_max"
              :title_number="$t('c_t_out_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel key="3" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-water-thermometer-outline</v-icon>
            <span>{{ $t("dhw_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.water_set !== undefined"
              :value="deviceData.mb.water_set == 0 ? false : true"
              :title_number="$t('water_set')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              controlVariable="water_set"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.disinf_set !== undefined"
              :value="deviceData.mb.disinf_set == 0 ? false : true"
              :title_number="$t('disinf_set')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              controlVariable="disinf_set"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.t_dhw_set !== undefined"
              v-model="deviceData.mb.t_dhw_set"
              :min="deviceActionLimits.t_dhw_set.min || 20"
              :max="deviceActionLimits.t_dhw_set.max || 60"
              :step="1"
              :decimals="0"
              controlVariable="t_dhw_set"
              :title_number="$t('t_dhw_set')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.dhw_on_dt !== undefined"
              v-model="deviceData.mb.dhw_on_dt"
              :min="deviceActionLimits.dhw_on_dt.min || 1"
              :max="deviceActionLimits.dhw_on_dt.max || 30"
              :step="1"
              :decimals="0"
              controlVariable="dhw_on_dt"
              :title_number="$t('dhw_on_dt')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.dhw_dt_set !== undefined"
              v-model="deviceData.mb.dhw_dt_set"
              :min="deviceActionLimits.dhw_dt_set.min || 5"
              :max="deviceActionLimits.dhw_dt_set.max || 40"
              :step="1"
              :decimals="0"
              controlVariable="dhw_dt_set"
              :title_number="$t('dhw_dt_set')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.dhw_t_amb_max !== undefined"
              v-model="deviceData.mb.dhw_t_amb_max"
              :min="deviceActionLimits.dhw_t_amb_max.min || 35"
              :max="deviceActionLimits.dhw_t_amb_max.max || 43"
              :step="1"
              :decimals="0"
              controlVariable="dhw_t_amb_max"
              :title_number="$t('dhw_t_amb_max')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.dhw_t_amb_min !== undefined"
              v-model="deviceData.mb.dhw_t_amb_min"
              :min="deviceActionLimits.dhw_t_amb_min.min || -25"
              :max="deviceActionLimits.dhw_t_amb_min.max || 5"
              :step="1"
              :decimals="0"
              controlVariable="dhw_t_amb_min"
              :title_number="$t('dhw_t_amb_min')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel key="4" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-chart-bell-curve</v-icon>
            <span>{{ $t("weather_adaptation_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.h_cur_en !== undefined"
              :value="deviceData.mb.h_cur_en == 0 ? false : true"
              :title_number="$t('h_cur_en')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              cols="6"
              controlVariable="h_cur_en"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSwitchCard
              v-if="deviceData.mb.c_cur_en !== undefined"
              :value="deviceData.mb.c_cur_en == 0 ? false : true"
              :title_number="$t('c_cur_en')"
              text_font_size="18px"
              control="other"
              subTitle="-"
              cols="6"
              controlVariable="c_cur_en"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSwitchCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.h_cur !== undefined"
              v-model="deviceData.mb.h_cur"
              :min="deviceActionLimits.h_cur.min || 1"
              :max="deviceActionLimits.h_cur.max || 9"
              :step="1"
              :decimals="0"
              cols="6"
              controlVariable="h_cur"
              :title_number="$t('h_cur')"
              :disabled=false
              unit=""
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.c_cur !== undefined"
              v-model="deviceData.mb.c_cur"
              :min="deviceActionLimits.c_cur.min || 1"
              :max="deviceActionLimits.c_cur.max || 9"
              :step="1"
              :decimals="0"
              cols="6"
              controlVariable="c_cur"
              unit=""
              :title_number="$t('c_cur')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel key="5" class="whitesmoke" >
        <v-expansion-panel-header>
          <div>
            <v-icon color="text-color" class="mr-3">mdi-resistor</v-icon>
            <span>{{ $t("auxiliary_heating_system_control") }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content >
          <v-row v-if="deviceData.mb !== undefined" class="whitesmoke" no-gutters>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ahs_t_on !== undefined"
              v-model="deviceData.mb.ahs_t_on"
              :min="deviceActionLimits.ahs_t_on.min || -15"
              :max="deviceActionLimits.ahs_t_on.max || 30"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ahs_t_on"
              :title_number="$t('ahs_t_on')"
              :disabled=false
              unit=""
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ahs_dt_on !== undefined"
              v-model="deviceData.mb.ahs_dt_on"
              :min="deviceActionLimits.ahs_dt_on.min || 2"
              :max="deviceActionLimits.ahs_dt_on.max || 20"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ahs_dt_on"
              unit=""
              :title_number="$t('ahs_dt_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ahs_dt_dly !== undefined"
              v-model="deviceData.mb.ahs_dt_dly"
              :min="deviceActionLimits.ahs_dt_dly.min || 5"
              :max="deviceActionLimits.ahs_dt_dly.max || 120"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ahs_dt_dly"
              unit=""
              :title_number="$t('ahs_dt_dly')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ibh_t_on !== undefined"
              v-model="deviceData.mb.ibh_t_on"
              :min="deviceActionLimits.ibh_t_on.min || -15"
              :max="deviceActionLimits.ibh_t_on.max || 10"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ibh_t_on"
              unit=""
              :title_number="$t('ibh_t_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ibh_dt_on !== undefined"
              v-model="deviceData.mb.ibh_dt_on"
              :min="deviceActionLimits.ibh_dt_on.min || 2"
              :max="deviceActionLimits.ibh_dt_on.max || 10"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ibh_dt_on"
              unit=""
              :title_number="$t('ibh_dt_on')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
            <DeviceOverviewSliderCard
              v-if="deviceData.mb.ibh_dt_dly !== undefined"
              v-model="deviceData.mb.ibh_dt_dly"
              :min="deviceActionLimits.ibh_dt_dly.min || 15"
              :max="deviceActionLimits.ibh_dt_dly.max || 120"
              :step="1"
              :decimals="0"
              cols="4"
              controlVariable="ibh_dt_dly"
              unit=""
              :title_number="$t('ibh_dt_dly')"
              :disabled=false
              text_font_size="18px"
              control="other"
              @defaultAction="defaultAction"
            >
            </DeviceOverviewSliderCard>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
  </v-expansion-panels>
</template>


<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import { TimeUtil } from '@/helpers/TimeUtil'
import DashboardCardBarChart from "@/components/Cards/DashboardCardBarChart.vue";
import DashboardCardPieChart from "@/components/Cards/DashboardCardPieChart.vue";
import DeviceOverviewCard from "@/components/Cards/DeviceOverviewCard.vue";
import DeviceOverviewSwitchCard from "@/components/Cards/DeviceOverviewSwitchCard.vue";
import DeviceOverviewButtonCard from "@/components/Cards/DeviceOverviewButtonCard.vue";
import DeviceOverviewHeatingBalanceSliderCard from "@/components/Cards/DeviceOverviewHeatingBalanceSliderCard.vue";
import DeviceOverviewSliderCard from "@/components/Cards/DeviceOverviewSliderCard.vue";
import DashboardCardFaultsPieChart from "@/components/Cards/DashboardCardFaultsPieChart.vue";
import DeviceOverviewRoomTargetCard from "@/components/Cards/DeviceOverviewRoomTargetCard.vue";
import DeviceOverviewRoomTargetLimitsCard from "@/components/Cards/DeviceOverviewRoomTargetLimitsCard.vue";
import DeviceOverviewSelectCard from "@/components/Cards/DeviceOverviewSelectCard.vue"

import { devicesModule } from '@/store/modules/store-accessor'
import {
  getTCh1SetLimits,
  getHeatingSetLimits,
  getCoolingSetLimits,
} from '@/helpers/heatpump-limits'

@Component({
  components: {
    DashboardCardBarChart,
    DashboardCardPieChart,
    DeviceOverviewCard,
    DeviceOverviewSwitchCard,
    DeviceOverviewButtonCard,
    DeviceOverviewHeatingBalanceSliderCard,
    DeviceOverviewSliderCard,
    DeviceOverviewRoomTargetCard,
    DeviceOverviewRoomTargetLimitsCard,
    DashboardCardFaultsPieChart,
    DeviceOverviewSelectCard
  }
})

export default class MBControlView
 extends Vue {
  @Prop() device: Record<string, any>;

  flag = false;

  expansionPanel = [0];
  terminalUnitList = [
    {"name": "FCU", "value": 0},
    {"name": "RAD", "value": 1},
    {"name": "FLH", "value": 2},
  ];

  controlModeList = [
    {"name": "AUTO", "value": 1},
    {"name": "COOLING", "value": 2},
    {"name": "HEATING", "value": 3},
  ];

  // TODO This need more info & Testing
  // heatpumControlModelList = [
  //   {"name": "T_R", "value": 1},
  //   {"name": "Setpoint", "value": 2},
  //   {"name": "T_DWH", "value": 2},
  // ];

  get DHWStatus() {
    return this.device.data.properties.mb.ctl_mod_dhw === 1? true : false
  }

  get deviceData () {
    return this.device.data.properties
  }

  get deviceActionLimits () {
    const limits = this.device.info.hvac_action_limits_json as any || {};
    
    // Create a proxy that returns safe empty objects for any undefined properties
    return new Proxy(limits, {
      get(target, prop) {
        if (target[prop]) {
          return target[prop];
        }
        // Return an empty object with min/max properties to prevent undefined access
        return { min: undefined, max: undefined };
      }
    });
  }

  get tCh1SetLimits () {
    return getTCh1SetLimits(
      this.deviceData?.mb?.op_mode_set ?? this.deviceData?.mb?.op_set,
      this.deviceData?.mb?.op_mode,
      this.deviceData?.mb?.h_ter_typ_ch1,
      this.deviceData?.mb?.c_ter_typ_ch1
    );
  }

  get tCh1SetDisabled () {
    return this.tCh1SetLimits.disabled === true;
  }

  get heatingSetLimits () {
    return getHeatingSetLimits(
      this.deviceData?.mb?.h_ter_typ_ch1,
      this.deviceData?.mb?.c_ter_typ_ch1
    );
  }

  get coolingSetLimits () {
    return getCoolingSetLimits(
      this.deviceData?.mb?.h_ter_typ_ch1,
      this.deviceData?.mb?.c_ter_typ_ch1
    );
  }

  get coolingSetDisabled () {
    return this.coolingSetLimits.disabled === true;
  }

  get deviceId () {
    return this.device.info.device_id
  }

  formatDate (date: Date): string {
    return TimeUtil.formatDateTime(date)
  }

  get heatpumpCtlMod() {
    let mode = (this.deviceData?.mb?.ctl_mod_t_r || this.deviceData?.mb?.ctl_mod_t_ch1 || this.deviceData?.mb?.ctl_mod_t_ch2) ? true : false
    
    return mode
  }

  async heatpumpControlChange(val: number) : Promise<void> {
    await devicesModule.getDeviceInfo(this.deviceId);

    let data = {
        action: "ctl_mod",
        action_value: 0,
    }

    if (val) {
      if (this.deviceData?.mb?.ctl_mod_dhw == 1) {
        data.action_value = 7; // This enables only the Heating for Zone 1 or Room thermostat (Zone 2 + DHW are not controlled)
      }
      else {
        data.action_value = 3; // This enables only the Heating for Zone 1 or Room thermostat (Zone 2 + DHW are not controlled)
      }
    }
    else {
      if(this.deviceData?.mb?.ctl_mod_dhw == 1) {
        data.action_value = 4; // This turns off the heatpump completely
      }
      else {
        data.action_value = 0; // This turns off the heatpump completely
      }
    }

    devicesModule.updateDeviceConfig(data);
  }

  DHWControlChange(val: number) : void {
    let data = {
        action: "ctl_mod_dhw",
        action_value: val,
    }

    if (val) {
      data.action_value = 1;
    }
    else {
      data.action_value = 0;
    }
    devicesModule.updateDeviceConfig(data);
  }


  // heatpumpControlModeChange(val: number) : void {
  //   let data = {
  //       action: "ctl_mod",
  //       action_value: 0,
  //   }

  //   if (val === 0) {
  //     data.action_value = 0;
  //   }
  //   else {
  //     data.action_value = 7;
  //   }
     
  //   devicesModule.updateDeviceConfig(data);
  // }

  fixedBoilerTemperatureChange(val: string) : void {
    let data = {
        action: "t_set",
        action_value: val,
    }
    devicesModule.updateDeviceConfig(data);
  }

  defaultAction(val: string, action: string) : void {
    let data = {
        action: action,
        action_value: val,
    }

    devicesModule.updateDeviceConfig(data);

  }

};
</script>

<style scoped lang="scss">
.tab {
  overflow-y: auto;
}

div { 
    width:100%;
}
</style>
